// src/types/ReportingDashboard/elements.types.ts
import PptxGenJS from "pptxgenjs"
import { RTableContent } from "types/engineOutput.types"

// --- Enums ---
export enum STATISTIC_TYPE {
  // XTAB types (for BOOL and non-INT variables)
  N = "n",
  BIG_N = "N",
  ROW_PERCENT = "row%",
  COL_PERCENT = "col%",
  INDEX = "index",
  PERC_PERCENT = "perc(%)",

  // DTAB types (for INT variables)
  AVG = "avg",
  BASE_N = "base n",
  PERC = "perc",
  CRR_PERCENT = "CRR%",
  MIN = "min",
  MAX = "max",
  /** Standard deviation of the values; only when OPERATORS: names it. */
  STDDEV = "stddev",
  /** Standard error of the mean; only when OPERATORS: names it. */
  STDERR = "stderr",
  /** Median of the values (weighted when weighted); only when OPERATORS: names it. */
  MEDIAN = "median",
  /** Sum of the values (weighted when weighted); only when OPERATORS: names it. */
  SUM = "sum",
}

// DTAB-specific statistic types
export const DTAB_STATISTIC_TYPES = [
  STATISTIC_TYPE.AVG,
  STATISTIC_TYPE.BASE_N,
  STATISTIC_TYPE.PERC,
  STATISTIC_TYPE.CRR_PERCENT,
  STATISTIC_TYPE.MIN,
  STATISTIC_TYPE.MAX,
  STATISTIC_TYPE.STDDEV,
  STATISTIC_TYPE.STDERR,
  STATISTIC_TYPE.MEDIAN,
  STATISTIC_TYPE.SUM,
] as const

export enum CHART_DISPLAY_TYPE {
  BAR = "bar",
  HORIZONTAL_BAR = "horizontal",
}

export enum TABLE_OPERATION_TYPE {
  XTAB = "XTAB",
  DTAB = "DTAB",
}

export enum LEGEND_POSITION {
  RIGHT = "right",
  LEFT = "left",
  TOP = "top",
  BOTTOM = "bottom",
  NONE = "none",
}
export enum TEXT_ALIGN {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
}
export enum ELEMENT_TYPE {
  TEXT = "text",
  HEADING = "heading",
  IMAGE = "image",
  TABLE = "table",
  CHART = "chart",
  PIE_CHART = "piechart",
  TIME_SERIES_CHART = "timeserieschart",
  DEFAULT = "default",
}

// --- Base Slide Element Properties ---
export interface BaseSlideElement {
  id: string
  type: ELEMENT_TYPE
  x: number
  y: number
  width?: number
  height?: number
  isTemplateElement?: boolean
  text?: string
  fontSize?: number
  color?: string
  isBold?: boolean
  isItalic?: boolean
  isUnderline?: boolean
  textAlign?: TEXT_ALIGN
}
export interface TextSlideElementProps extends BaseSlideElement {
  type: ELEMENT_TYPE.TEXT | ELEMENT_TYPE.HEADING
  text: string
}
export interface ImageSlideElementProps extends BaseSlideElement {
  type: ELEMENT_TYPE.IMAGE
  imageSrc: string | null
}
export interface TableSlideElementProps extends BaseSlideElement {
  type: ELEMENT_TYPE.TABLE
}
export interface ChartSlideElementProps extends BaseSlideElement {
  type:
    | ELEMENT_TYPE.CHART
    | ELEMENT_TYPE.PIE_CHART
    | ELEMENT_TYPE.TIME_SERIES_CHART
}
export type SlideElement =
  | TextSlideElementProps
  | ImageSlideElementProps
  | TableSlideElementProps
  | ChartSlideElementProps

// --- Stored Configuration Settings for Elements ---
interface BaseElementSettings {
  id?: string
  width?: number
  height?: number
}
interface BaseChartSettingsConfig extends BaseElementSettings {
  selectedStatisticType?: STATISTIC_TYPE
  chartTitle?: string
  chartDataUri?: string | null
}
export interface ChartElementSettings extends BaseChartSettingsConfig {
  selectedVariable: string | null
  breakdownVariable: string | null
  chartType?: CHART_DISPLAY_TYPE
  showTotals?: boolean
  dashboardSelectedKeys?: string[]
  dashboardSelectedCategories?: string[]
  tableOperationType: TABLE_OPERATION_TYPE
}
export interface PieChartElementSettings extends BaseChartSettingsConfig {
  selectedVariable: string | null
  legendPosition?: LEGEND_POSITION
  hideArcLabels?: boolean
  dashboardSelectedCategories?: string[]
}
export interface TimeSeriesChartSettings extends BaseElementSettings {
  selectedPrimaryVariable: string | null
  selectedBreakdownVariable: string | null
  selectedWaveVariable: string | null
  selectedStatisticType?: STATISTIC_TYPE
  chartDataUri?: string | null
  selectedDashboardSeries?: string[]
  selectedDashboardWaves?: string[]
  tableOperationType: TABLE_OPERATION_TYPE
}
export interface TableElementSettings extends BaseElementSettings {
  crossTabVariables: (string | null)[]
  selectedOperators?: STATISTIC_TYPE[]
  isTransposed?: boolean
  originalWidth?: number | null
  tableOperationType?: TABLE_OPERATION_TYPE
}
export interface TextElementSettings extends BaseElementSettings {
  text?: string
  fontSize?: number
  color?: string
  isBold?: boolean
  isItalic?: boolean
  isUnderline?: boolean
  textAlign?: TEXT_ALIGN
}
export interface ImageElementSettings extends BaseElementSettings {
  imageSrc?: string | null
  alt?: string
}
export type DataElementConfig =
  | ChartElementSettings
  | PieChartElementSettings
  | TimeSeriesChartSettings
  | TableElementSettings
  | TextElementSettings
  | ImageElementSettings

// --- Service/Utility Payloads ---
export interface ChartRenderDataPayload {
  content: RTableContent
}
export interface ElementConfigForBatchImage {
  elementId: string
  elementType:
    | ELEMENT_TYPE.CHART
    | ELEMENT_TYPE.PIE_CHART
    | ELEMENT_TYPE.TIME_SERIES_CHART
  config:
    | ChartElementSettings
    | PieChartElementSettings
    | TimeSeriesChartSettings
}

// PPTX Export Specific Payloads
export interface TableCellDataContentForPptx {
  data: (string | number | null)[][]
  headers?: (string | { text: string; options?: PptxGenJS.TextPropsOptions })[]
}
export interface TableRuntimeDataForPptx {
  content?: TableCellDataContentForPptx
}

// Fix: Create a separate interface for export that doesn't inherit the conflicting tableData property
export interface TableExportPayload {
  id: string
  crossTabVariables: (string | null)[]
  selectedOperators?: STATISTIC_TYPE[]
  isTransposed?: boolean
  originalWidth?: number | null
  width?: number
  height?: number
  tableData?: TableRuntimeDataForPptx // For PPTX export - different from runtime tableData
  tableOperationType?: TABLE_OPERATION_TYPE
}

export interface ImageDimensions {
  width: number
  height: number
}
